import { useEffect, useState } from 'react';
import {
  INSIGHTS_CHART_TYPES,
  insightsDimensionLabel,
  insightsDomainCatalog,
  insightsMeasureFormat,
  insightsMeasureLabel,
  type InsightsCard,
  type InsightsChartType,
  type InsightsDomain,
  type InsightsMeasureFormat,
  type InsightsQueryFilters,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { requeryInsightsCard } from './api.js';
import { DonutChart, HBarChart, LineChart, StackedBarChart, type StackedSeries } from './charts.js';

interface InsightsCardViewProps {
  card: InsightsCard;
  /** Opens the drill-down modal with the given merged filters. */
  onDrill: (domain: InsightsDomain, filters: InsightsQueryFilters, title: string) => void;
  /** Pin state + toggle (omitted where pinning makes no sense, e.g. Member lens). */
  pinned?: boolean;
  onTogglePin?: (card: InsightsCard) => void;
}

/** Sentinel key the engine uses for rolled-up low-ranked groups. */
const OTHER_KEY = '__other__';

/**
 * One AI answer card: the chart the model chose, a toggleable data table, and
 * drill-down to the underlying time entries (whole card or a clicked group).
 */
export function InsightsCardView({
  card: initial,
  onDrill,
  pinned,
  onTogglePin,
}: InsightsCardViewProps): JSX.Element {
  const [showData, setShowData] = useState(false);
  // Local copy so widget actions (requery/chart swaps) update this card in place.
  const [card, setCard] = useState(initial);
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  useEffect(() => setCard(initial), [initial]);

  const { result, chart } = card;
  const { dimensions, measures, filters } = result.spec;
  const domain = result.spec.domain ?? 'time';
  const format = measureFormatter(insightsMeasureFormat(domain, chart.measure));

  /** Re-runs the query server-side (no model call) and swaps the card in place. */
  const requery = async (patch: {
    measures?: string[];
    filters?: InsightsQueryFilters;
    chartMeasure?: string;
  }): Promise<void> => {
    setWorking(true);
    setActionError(null);
    try {
      const next = await requeryInsightsCard({
        domain: result.spec.domain,
        dimensions,
        measures: patch.measures ?? measures,
        filters: patch.filters ?? filters,
        limit: result.spec.limit,
        chart: { ...chart, measure: patch.chartMeasure ?? chart.measure },
        title: card.title,
      });
      setCard({ ...next, id: card.id });
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not update the chart.');
    } finally {
      setWorking(false);
    }
  };

  const changeMeasure = (measure: string): void => {
    if (measures.includes(measure)) {
      setCard((c) => ({ ...c, chart: { ...c.chart, measure } }));
    } else {
      void requery({ measures: [...measures, measure], chartMeasure: measure });
    }
  };

  // Doubles the window backwards from the resolved concrete dates.
  const widened = widenFilters(filters);

  const drillRow = (rowIndex: number): void => {
    const row = result.rows[rowIndex];
    if (!row) return;
    let merged: InsightsQueryFilters = { ...filters };
    let ok = true;
    dimensions.forEach((dim, i) => {
      const extra = filtersForKey(domain, dim, row.keys[i] ?? null);
      if (extra === null) ok = false;
      else merged = { ...merged, ...extra };
    });
    if (ok) onDrill(domain, merged, `${card.title} — ${row.labels.join(' · ')}`);
  };

  const rowDrillable = (rowIndex: number): boolean => {
    const row = result.rows[rowIndex];
    if (!row) return false;
    return dimensions.every((dim, i) => filtersForKey(domain, dim, row.keys[i] ?? null) !== null);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-slate-900">{card.title}</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {describeCard(domain, dimensions, chart.measure)} ·{' '}
            {result.scannedEntries.toLocaleString()} records
            {filters.keywords && filters.keywords.length > 0
              ? ` · matching “${filters.keywords.join('”, “')}”`
              : ''}
            {result.limited ? ' · top groups shown' : ''}
            {result.truncated ? ' · scan capped' : ''}
          </p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          {onTogglePin ? (
            <button
              type="button"
              onClick={() => onTogglePin(card)}
              className={`rounded-lg border px-2 py-1 text-xs font-medium ${
                pinned
                  ? 'border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              title={pinned ? 'Unpin from the top of the chat' : 'Keep this chart visible at the top'}
            >
              {pinned ? 'Pinned' : 'Pin'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setShowData((v) => !v)}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            {showData ? 'Chart' : 'Data'}
          </button>
          <button
            type="button"
            onClick={() => onDrill(domain, filters, card.title)}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            View records
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <select
          value={chart.type}
          onChange={(e) =>
            setCard((c) => ({ ...c, chart: { ...c.chart, type: e.target.value as InsightsChartType } }))
          }
          className="rounded-lg border border-slate-200 bg-white px-1.5 py-1"
          title="Chart type"
        >
          {INSIGHTS_CHART_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace('_', ' ')}
            </option>
          ))}
        </select>
        <select
          value={chart.measure}
          onChange={(e) => changeMeasure(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-1.5 py-1"
          title="Measure"
        >
          {insightsDomainCatalog(domain).measures.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
        {widened ? (
          <button
            type="button"
            onClick={() => void requery({ filters: widened })}
            className="rounded-lg border border-slate-200 px-2 py-1 font-medium hover:bg-slate-50"
            title="Double the date window backwards and re-run"
          >
            Widen range ×2
          </button>
        ) : null}
        {working ? <Spinner /> : null}
        {actionError ? <span className="text-rose-600">{actionError}</span> : null}
      </div>

      {showData ? (
        <DataTable card={card} onDrillRow={drillRow} rowDrillable={rowDrillable} />
      ) : (
        <CardChart card={card} format={format} onSelect={drillRow} />
      )}

      <p className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-500">
        {measures
          .map(
            (m) =>
              `${insightsMeasureLabel(domain, m)}: ${measureFormatter(insightsMeasureFormat(domain, m))(result.totals[m] ?? 0)}`,
          )
          .join(' · ')}
      </p>
    </div>
  );
}

function CardChart({
  card,
  format,
  onSelect,
}: {
  card: InsightsCard;
  format: (v: number) => string;
  onSelect: (rowIndex: number) => void;
}): JSX.Element {
  const { result, chart } = card;
  const dims = result.spec.dimensions.length;
  const measure = chart.measure;
  const domain = result.spec.domain ?? 'time';

  if (result.rows.length === 0) {
    return <p className="py-6 text-center text-sm text-slate-500">No matching records.</p>;
  }

  if (chart.type === 'table' || dims === 0) {
    return <DataTable card={card} />;
  }

  if (dims === 1) {
    const rows = result.rows.map((r) => ({
      label: r.labels[0] ?? '',
      value: r.values[measure] ?? 0,
    }));
    if (chart.type === 'pie') {
      return <DonutChart slices={rows} format={format} onSelect={onSelect} />;
    }
    if (chart.type === 'line') {
      return (
        <LineChart
          categories={rows.map((r) => r.label)}
          series={[
            { label: insightsMeasureLabel(domain, measure), values: rows.map((r) => r.value) },
          ]}
          format={format}
        />
      );
    }
    return <HBarChart rows={rows} format={format} onSelect={onSelect} />;
  }

  // Two dimensions → pivot: x = dimension 0, series = dimension 1.
  const { categories, series, categoryRowIndex } = pivot(card, measure);
  if (chart.type === 'line') {
    return <LineChart categories={categories} series={series} format={format} />;
  }
  return (
    <StackedBarChart
      categories={categories}
      series={series}
      format={format}
      onSelect={(ci) => {
        const rowIndex = categoryRowIndex[ci];
        if (rowIndex !== undefined) onSelect(rowIndex);
      }}
    />
  );
}

function DataTable({
  card,
  onDrillRow,
  rowDrillable,
}: {
  card: InsightsCard;
  onDrillRow?: (rowIndex: number) => void;
  rowDrillable?: (rowIndex: number) => boolean;
}): JSX.Element {
  const { dimensions, measures } = card.result.spec;
  const domain = card.result.spec.domain ?? 'time';
  return (
    <div className="max-h-80 overflow-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            {dimensions.map((d) => (
              <th key={d} className="py-1.5 pr-3 font-medium">
                {insightsDimensionLabel(domain, d)}
              </th>
            ))}
            {measures.map((m) => (
              <th key={m} className="py-1.5 pr-3 text-right font-medium">
                {insightsMeasureLabel(domain, m)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {card.result.rows.map((row, ri) => {
            const clickable = Boolean(onDrillRow && rowDrillable?.(ri));
            return (
              <tr
                key={ri}
                className={`border-b border-slate-100 ${clickable ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                onClick={clickable ? () => onDrillRow!(ri) : undefined}
              >
                {row.labels.map((label, li) => (
                  <td key={li} className="max-w-64 truncate py-1.5 pr-3 text-slate-700" title={label}>
                    {label}
                  </td>
                ))}
                {measures.map((m) => (
                  <td key={m} className="py-1.5 pr-3 text-right tabular-nums text-slate-900">
                    {measureFormatter(insightsMeasureFormat(domain, m))(row.values[m] ?? 0)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers (pure)
// ---------------------------------------------------------------------------

/** Pivots two-dimension rows into categories (dim 0) × series (dim 1). */
function pivot(
  card: InsightsCard,
  measure: string,
): { categories: string[]; series: StackedSeries[]; categoryRowIndex: Record<number, number> } {
  const categories: string[] = [];
  const categoryIndex = new Map<string, number>();
  const seriesIndex = new Map<string, number>();
  const categoryRowIndex: Record<number, number> = {};

  for (const [ri, row] of card.result.rows.entries()) {
    const catKey = row.keys[0] ?? '';
    if (!categoryIndex.has(catKey)) {
      categoryIndex.set(catKey, categories.length);
      categoryRowIndex[categories.length] = ri;
      categories.push(row.labels[0] ?? '');
    }
    const serKey = row.labels[1] ?? '';
    if (!seriesIndex.has(serKey)) seriesIndex.set(serKey, seriesIndex.size);
  }

  const series: StackedSeries[] = [...seriesIndex.keys()].map((label) => ({
    label,
    values: new Array<number>(categories.length).fill(0),
  }));
  for (const row of card.result.rows) {
    const ci = categoryIndex.get(row.keys[0] ?? '')!;
    const si = seriesIndex.get(row.labels[1] ?? '')!;
    series[si]!.values[ci] = row.values[measure] ?? 0;
  }
  return { categories, series, categoryRowIndex };
}

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const CURRENCY_CENTS = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function measureFormatter(format: InsightsMeasureFormat): (value: number) => string {
  switch (format) {
    case 'currency':
      return (v) => (Math.abs(v) >= 1000 ? CURRENCY.format(v) : CURRENCY_CENTS.format(v));
    case 'count':
      return (v) => v.toLocaleString();
    case 'percent':
      return (v) => `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case 'days':
      return (v) => `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} d`;
    default:
      return (v) => `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} h`;
  }
}

function describeCard(domain: InsightsDomain, dimensions: string[], measure: string): string {
  const by = dimensions.map((d) => insightsDimensionLabel(domain, d)).join(' × ');
  const measureLabel = insightsMeasureLabel(domain, measure);
  return by ? `${measureLabel} by ${by}` : measureLabel;
}

/** Doubles the resolved date window backwards; null when the card has no window. */
function widenFilters(filters: InsightsQueryFilters): InsightsQueryFilters | null {
  if (!filters.dateFrom) return null;
  const from = new Date(`${filters.dateFrom}T00:00:00.000Z`);
  const to = filters.dateTo ? new Date(`${filters.dateTo}T00:00:00.000Z`) : new Date();
  const spanMs = Math.max(to.getTime() - from.getTime(), 86_400_000);
  const newFrom = new Date(from.getTime() - spanMs);
  return { ...filters, period: null, dateFrom: newFrom.toISOString().slice(0, 10) };
}

/**
 * Converts one clicked group key into extra drill filters (per domain). Returns null
 * when the group cannot be drilled (rolled-up "Other", missing links, dimensions with
 * no matching filter key).
 */
function filtersForKey(
  domain: InsightsDomain,
  dim: string,
  key: string | null,
): Partial<InsightsQueryFilters> | null {
  if (key === OTHER_KEY || key === null || key === '') return null;
  if (domain === 'tickets') {
    switch (dim) {
      case 'customer':
        return { customerIds: [key] };
      case 'assignee':
        return { memberIds: [key] };
      case 'month':
        return { dateFrom: `${key}-01`, dateTo: endOfMonth(key) };
      default:
        return null;
    }
  }
  if (domain === 'money') {
    switch (dim) {
      case 'customer':
        return { customerIds: [key] };
      case 'agreement':
        return { agreementIds: [key] };
      case 'serviceCategory':
        return { serviceCategoryIds: [key] };
      case 'month':
        return { dateFrom: `${key}-01`, dateTo: endOfMonth(key) };
      default:
        return null;
    }
  }
  if (domain === 'pipeline') {
    switch (dim) {
      case 'customer':
        return { customerIds: [key] };
      case 'owner':
        return { memberIds: [key] };
      case 'type':
        return { serviceCategoryIds: [key] };
      case 'closeMonth':
        return { dateFrom: `${key}-01`, dateTo: endOfMonth(key) };
      default:
        return null;
    }
  }
  switch (dim) {
    case 'member':
      return { memberIds: [key] };
    case 'customer':
      return { customerIds: [key] };
    case 'project':
      return { projectIds: [key] };
    case 'agreement':
      return { agreementIds: [key] };
    case 'ticket':
      return { ticketIds: [key] };
    case 'serviceCategory':
      return { serviceCategoryIds: [key] };
    case 'billingClass':
      return { billingClassIds: [key] };
    case 'billable':
      return { billable: key === 'true' };
    case 'day':
      return { dateFrom: key, dateTo: key };
    case 'week':
      return { dateFrom: key, dateTo: addDays(key, 6) };
    case 'month':
      return { dateFrom: `${key}-01`, dateTo: endOfMonth(key) };
    case 'quarter': {
      const year = Number(key.slice(0, 4));
      const q = Number(key.slice(6, 7));
      const startMonth = (q - 1) * 3 + 1;
      const endMonth = `${year}-${String(startMonth + 2).padStart(2, '0')}`;
      return {
        dateFrom: `${year}-${String(startMonth).padStart(2, '0')}-01`,
        dateTo: endOfMonth(endMonth),
      };
    }
    case 'year':
      return { dateFrom: `${key}-01-01`, dateTo: `${key}-12-31` };
    default:
      return null;
  }
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function endOfMonth(yearMonth: string): string {
  const year = Number(yearMonth.slice(0, 4));
  const month = Number(yearMonth.slice(5, 7));
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}
